
import React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Command, 
  CommandEmpty, 
  CommandGroup, 
  CommandInput, 
  CommandItem,
  CommandList 
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SectorFilterProps {
  sectors: string[];
  selectedSector: string | null;
  onSelectSector: (sector: string | null) => void;
  isLoading?: boolean;
}

const SectorFilter: React.FC<SectorFilterProps> = ({
  sectors,
  selectedSector,
  onSelectSector,
  isLoading = false,
}) => {
  const [open, setOpen] = React.useState(false);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectSector(null);
  };

  const safeSectors = Array.isArray(sectors) ? sectors : [];

  const isActive = !!selectedSector;

  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "justify-between rounded-full h-8 px-3 text-xs border transition-all",
              isActive
                ? "bg-primary/20 border-primary/40 text-foreground hover:bg-primary/30"
                : "bg-white/5 border-white/10 hover:bg-white/10 text-muted-foreground"
            )}
            disabled={isLoading}
          >
            <span className="truncate">
              {selectedSector || (isLoading ? "Loading..." : "Sector")}
            </span>
            {isActive ? (
              <span
                role="button"
                aria-label="Clear sector"
                className="ml-1.5 p-0.5 rounded-full hover:bg-white/20 transition-colors"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectSector(null);
                  setOpen(false);
                }}
              >
                <X className="h-3 w-3 shrink-0" />
              </span>
            ) : (
              <ChevronDown className="ml-1.5 h-3 w-3 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0">
          <Command>
            <CommandInput placeholder="Search sector..." />
            <CommandList>
              <CommandEmpty>No sector found.</CommandEmpty>
              <CommandGroup>
                {safeSectors.map((sector) => (
                  <CommandItem
                    key={sector}
                    value={sector}
                    onSelect={() => {
                      onSelectSector(sector);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedSector === sector ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {sector}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SectorFilter;
